import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import invariant from 'tiny-invariant';

import { Providers } from '~app/providers';
import { queryClient } from '~app/query-client.ts';

import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useCurrentUser } from '~entities/users/model/user-store';

import { ErrorFallback } from '~shared/observability/error-fallback';
import {
  setSentryStudioContext,
  setSentryUserContext,
} from '~shared/observability/identity';
import { RouteErrorComponent } from '~shared/observability/route-error';
import { applyCrmEmbedScrollportClass } from '~shared/utils/apply-crm-embed-scrollport';

import './index.css';
import { routeTree } from './routeTree.gen.ts';

import * as Sentry from '@sentry/react';

const rootElement = document.getElementById('root');

invariant(rootElement, 'Root element not found');

// ENR-4098: before first paint when framed by CRM, own the iframe scrollport.
applyCrmEmbedScrollportClass();

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    // loginStore: {
    //   accessToken: null,
    //   parent: null,
    // },
  },
  // Branded, Sentry-reported crash UI for errors thrown while rendering a
  // route (these are caught here before reaching the top-level ErrorBoundary).
  defaultErrorComponent: RouteErrorComponent,
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Sentry initialization (after the router so tracing can hook into it)
// Skip local / development / staging (same as BE)
const sentryEnv = import.meta.env.VITE_SENTRY_ENV as string | undefined;
if (
  sentryEnv &&
  sentryEnv !== 'local' &&
  sentryEnv !== 'development' &&
  sentryEnv !== 'staging' &&
  import.meta.env.VITE_SENTRY_DSN
) {
  // Production samples at 10% to control event volume; non-prod samples 100% for full visibility.
  const tracesSampleRate = sentryEnv === 'production' ? 0.1 : 1.0;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: sentryEnv,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    sendDefaultPii: true,
    integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
    tracesSampleRate,
    tracePropagationTargets: [
      'localhost',
      /^\//,
      // Anchor to protocol + host so Sentry trace headers are only attached to
      // our backend, never third-party domains whose URLs happen to contain
      // this substring.
      /^https?:\/\/enrollio-be.*\.appspot\.com(\/|$)/,
    ],
  });
}

// eslint-disable-next-line react-refresh/only-export-components
const InnerApp = () => {
  const currentUser = useCurrentUser();
  const currentStudio = useCurrentStudio();

  // Keep Sentry user/studio context in sync so every captured event is
  // attributable to a studio and user.
  useEffect(() => {
    setSentryUserContext(currentUser);
  }, [currentUser]);

  useEffect(() => {
    setSentryStudioContext(currentStudio);
  }, [currentStudio]);

  return (
    <Providers>
      <RouterProvider
        router={router}
        context={{
          queryClient,
          // loginStore,
        }}
      />
      <Toaster
        richColors
        position='top-right'
        expand
        visibleToasts={5}
        gap={12}
      />
    </Providers>
  );
};

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={(errorData) => (
        <ErrorFallback
          error={errorData.error}
          componentStack={errorData.componentStack}
          eventId={errorData.eventId}
          resetError={() => {
            errorData.resetError();
          }}
        />
      )}
    >
      <InnerApp />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
